import { SubmissionError } from '#helper/submission-error';
import {
  FETCH_USER,
  SUCCESS,
  UPDATE_USER_SETTINGS,
  SUCCESS_LOGIN,
  FETCH_ACCOUNT,
  FETCH_ACCOUNT_STARTED,
  FETCH_ACCOUNT_SUCCEEDED,
  FETCH_ACCOUNT_FAILED,
  FETCH_ACCOUNT_FAILED_UNAUTHORIZED,
  PATCH_ACCOUNT_TIME_ZONE,
  AUTH_CHALLENGE_SET
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { error, snackBarError } from '#action/fetch';
import UserLocalStorage from '#utils/user-local-storage';
import { history } from '#configureStore';
import { getRequestOptions } from '#helper';

export function fetchUser(userId) {
  return dispatch =>
    fetch(webPortalApi.buildApiUrl(`user/${userId}`), getRequestOptions())
      .then(r => {
        r.json().then(async data => {
          if (!r.ok) {
            const shallRedirect = [403, 404].includes(r.status);

            dispatch(
              snackBarError({
                message: 'Error in request',
                ...data
              })
            );

            if (shallRedirect) {
              history.push('/user');
            }
          }

          if (data.directProjects.length) {
            const projectIds = data.directProjects;
            const projectsRes = await fetch(webPortalApi.buildApiUrl(`project?modules=true&ids=${projectIds.join(',')}`), getRequestOptions());
            data.directProjects = await projectsRes.json();
          }

          return dispatch({
            type: FETCH_USER,
            payload: data
          });
        });
      })
      .catch(() =>
        dispatch(
          error({
            message: 'Error in request'
          })
        ));
}

export function updateUserSettings(userId, data) {
  return async dispatch => {
    const response = await fetch(webPortalApi.buildApiUrl(`user/${userId}`), {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...getRequestOptions()
    });

    if (!response.ok) {
      const errorData = await response.json();
      dispatch(
        snackBarError({
          message: "Profile wasn't updated",
          ...errorData
        })
      );
      throw new SubmissionError(errorData);
    }

    const responseData = await response.json();
    dispatch({
      type: UPDATE_USER_SETTINGS,
      payload: responseData
    });
    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Profile was saved successfully!'
      }
    });
  };
}

export function updateUserSecuritySettings(userId, { password, twoFactorAuth }) {
  return async dispatch => {
    const requests = [];

    if (password) {
      requests.push(
        fetch(webPortalApi.buildApiUrl(`user/${userId}`), {
          method: 'PATCH',
          body: JSON.stringify({ password }),
          ...getRequestOptions()
        })
      );
    }

    if (twoFactorAuth !== undefined && twoFactorAuth !== null) {
      requests.push(
        fetch(webPortalApi.buildApiUrl(`user/${userId}/two-factor-auth`), {
          method: 'PATCH',
          body: JSON.stringify({ twoFactorAuth }),
          ...getRequestOptions()
        })
      );
    }

    const responses = await Promise.all(requests);

    const errorResponses = await Promise.all(
      responses.map(async response => {
        if (!response.ok) {
          const errorData = await response.json();

          return errorData;
        }
        return null;
      })
    );

    const firstError = errorResponses.find(errorData => errorData !== null);
    if (firstError) {
      dispatch(
        snackBarError({
          message: "Security settings wasn't updated",
          ...firstError
        })
      );
      throw new SubmissionError(firstError);
    }
    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Security settings was successfully updated!'
      }
    });
  };
}

export function signInUser(data, socket) {
  return async dispatch => {
    try {
      const response = await fetch(webPortalApi.buildUrl('/signin'), {
        method: 'POST',
        body: JSON.stringify(data),
        ...getRequestOptions()
      });

      const result = await response.json();

      if (!response.ok) {
        dispatch(
          snackBarError({
            message: 'Check credentials.',
            ...result
          })
        );
        throw new SubmissionError(result);
      }

      const { token, challengeName } = result;

      if (challengeName) {
        const challenge = { ...result, challengeIssuedAt: new Date().toISOString() };
        dispatch({
          type: AUTH_CHALLENGE_SET,
          payload: challenge
        });
        sessionStorage.setItem('authChallenge', JSON.stringify(challenge));
        history.push('/auth-challenge');

        return;
      }
      localStorage.setItem('auth', token);

      dispatch({
        type: SUCCESS_LOGIN,
        payload: {
          message: 'Welcome!'
        }
      });

      if (result.directProjects.length) {
        const projectIds = result.directProjects;
        const projectsRes = await fetch(webPortalApi.buildApiUrl(`project?modules=true&ids=${projectIds.join(',')}`), getRequestOptions());
        result.directProjects = await projectsRes.json();
      }

      dispatch({
        type: FETCH_ACCOUNT,
        payload: result
      });

      socket.emit('authenticate', { token });

      const beforeLoginPath = localStorage.getItem('beforeLoginPath');
      if (beforeLoginPath) {
        if (beforeLoginPath === '/docs/api' || beforeLoginPath.includes('/demo/')) {
          window.location.replace(webPortalApi.buildUrl(beforeLoginPath));
        } else {
          setTimeout(() => {
            history.push(beforeLoginPath);
          }, 0);
        }
        localStorage.removeItem('beforeLoginPath');
      } else {
        setTimeout(() => {
          history.push('/');
        }, 0);
      }
    } catch (e) {
      console.error('Error during sign-in:', e);
      throw e;
    }
  };
}

export function resetPassword(data) {
  return dispatch =>
    fetch(webPortalApi.buildUrl('/resetpassword'), {
      method: 'POST',
      body: JSON.stringify(data),
      ...getRequestOptions()
    })
      .then(r => r.json().then(d => {
        if (!r.ok) {
          dispatch(
            snackBarError({
              message: 'HTTP error occurred',
              ...d
            })
          );
          throw new SubmissionError(d);
        }

        history.push('/forgotpassword/1');
      }));
}

export const fetchAccount = ({ ignoreLoading } = {}) => async dispatch => {
  try {
    const url = webPortalApi.buildApiUrl('account');

    // setting `requestStatus` to `loading` causes `<Router>` re-render.
    if (!ignoreLoading) {
      dispatch({
        type: FETCH_ACCOUNT_STARTED
      });
    }

    const res = await fetch(url, getRequestOptions());
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }

    if (!res.ok) {
      if (res.status === 401) {
        dispatch({
          type: FETCH_ACCOUNT_FAILED_UNAUTHORIZED
        });
        return;
      }
      throw await res.json();
    }

    const account = await res.json();

    if (account.directProjects.length) {
      const projectIds = account.directProjects;
      const projectsRes = await fetch(webPortalApi.buildApiUrl(`project?modules=true&ids=${projectIds.join(',')}`), getRequestOptions());
      account.directProjects = await projectsRes.json();
    }

    UserLocalStorage.setUser(account.id);

    dispatch({
      type: FETCH_ACCOUNT_SUCCEEDED,
      payload: account
    });
  } catch (e) {
    dispatch({
      type: FETCH_ACCOUNT_FAILED
    });
    dispatch(
      error(e)
    );
  }
};

export function addUser(data, callback) {
  return dispatch => fetch(webPortalApi.buildApiUrl('user/'), {
    method: 'POST',
    body: JSON.stringify(data),
    ...getRequestOptions()
  }).then(r => r.json().then(d => {
    if (!r.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...d
        })
      );
      throw new SubmissionError(d);
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'User was created successfully!'
      }
    });
    callback();
  }));
}

export function createUserPassword(data, callback) {
  return dispatch => fetch(webPortalApi.buildUrl('/signup/password'), {
    method: 'PATCH',
    body: JSON.stringify(data),
    ...getRequestOptions()
  }).then(r => r.json().then(async d => {
    if (!r.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...d
        })
      );
      throw new SubmissionError(d);
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Password created successfully!'
      }
    });

    const { token } = d;
    localStorage.setItem('auth', token);

    if (d.directProjects.length) {
      const projectIds = d.directProjects;
      const projectsRes = await fetch(webPortalApi.buildApiUrl(`project?modules=true&ids=${projectIds.join(',')}`), getRequestOptions());
      d.directProjects = await projectsRes.json();
    }

    dispatch({
      type: FETCH_ACCOUNT,
      payload: d
    });

    setTimeout(() => {
      history.push('/');
    }, 500);
    callback();
  }));
}

export const patchAccountTimeZone = timeZone => async dispatch => {
  try {
    await fetch(webPortalApi.buildApiUrl('account/timezone'), {
      method: 'PATCH',
      body: JSON.stringify({ timeZone }),
      ...getRequestOptions()
    });
    dispatch({
      type: PATCH_ACCOUNT_TIME_ZONE,
      payload: timeZone
    });
  } catch (e) {
    console.error(e);
  }
};
